import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDownIcon } from '@heroicons/react/24/outline';
import apiService from '../services/api';

interface Category {
  id: number;
  name: string;
  slug: string;
  type: string;
  is_active: boolean;
  children?: Category[];
}

interface CategoryNavigationProps {
  className?: string;
  isMobile?: boolean;
  onLinkClick?: () => void;
  isActive?: boolean;
}

const CategoryNavigation: React.FC<CategoryNavigationProps> = ({ 
  className = '', 
  isMobile = false, 
  onLinkClick,
  isActive = false
}) => {
  const [categories, setCategories] = useState<Category[]>([]);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [expandedCategories, setExpandedCategories] = useState<Set<number>>(new Set());

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      setLoading(true);
      const response = await apiService.get('/categories/public');
      
      if (response.success && response.data) {
        // Organize categories into hierarchy
        const sortByAdded = (a: any, b: any) => {
          if (a?.created_at && b?.created_at) {
            return new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
          }
          return (a?.id || 0) - (b?.id || 0);
        };

        const mainCategories = response.data
          .filter((cat: Category) => cat.type === 'category')
          .sort(sortByAdded);
        
        const subcategories = response.data
          .filter((cat: Category) => cat.type === 'subcategory')
          .sort(sortByAdded);
        
        // Group subcategories under their parent categories
        const hierarchicalCategories = mainCategories.map((mainCat: Category) => ({
          ...mainCat,
          children: subcategories
            .filter((subCat: any) => subCat.parent_id === (mainCat as any).id)
            .sort(sortByAdded)
        }));
        
        setCategories(hierarchicalCategories);
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCategoryClick = (slug: string) => {
    if (onLinkClick) onLinkClick();
    setIsDropdownOpen(false);
  };

  const toggleCategoryExpansion = (categoryId: number) => {
    setExpandedCategories(prev => {
      const newSet = new Set(prev);
      if (newSet.has(categoryId)) {
        newSet.delete(categoryId);
      } else {
        newSet.add(categoryId);
      }
      return newSet;
    });
  };

  if (isMobile) {
    return (
      <div className={className}>
        <div className="space-y-1">
          {loading ? (
            <div className="px-4 py-3 text-sm text-gray-500">Loading categories...</div>
          ) : (
            <div className="space-y-1">
              {categories.map((category) => (
                <div key={category.id}>
                  {/* Main Category - Only show expand button if has children */}
                  {category.children && category.children.length > 0 ? (
                    <button
                      onClick={() => toggleCategoryExpansion(category.id)}
                      className="w-full flex items-center justify-between px-4 py-3 text-base font-medium text-gray-700 hover:bg-gray-100 hover:text-gray-900 rounded-xl transition-all duration-200"
                    >
                      <div className="flex items-center">
                        <svg className="w-5 h-5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.568 3H5.25A2.25 2.25 0 003 5.25v4.318c0 .597.237 1.17.659 1.591l9.581 9.581c.699.699 1.78.872 2.607.33a18.095 18.095 0 005.223-5.223c.542-.827.369-1.908-.33-2.607L11.16 3.66A2.25 2.25 0 009.568 3z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 6h.008v.008H6V6z" />
                        </svg>
                        {category.name}
                      </div>
                      <ChevronDownIcon 
                        className={`w-4 h-4 text-gray-400 transition-transform duration-200 ${
                          expandedCategories.has(category.id) ? 'rotate-180' : 'rotate-0'
                        }`} 
                      />
                    </button>
                  ) : (
                    <Link
                      to={`/product-signature?category=${category.slug}`}
                      className="flex items-center px-4 py-3 text-base font-medium text-gray-700 hover:bg-gray-100 hover:text-gray-900 rounded-xl transition-all duration-200"
                      onClick={() => handleCategoryClick(category.slug)}
                    >
                      <svg className="w-5 h-5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.568 3H5.25A2.25 2.25 0 003 5.25v4.318c0 .597.237 1.17.659 1.591l9.581 9.581c.699.699 1.78.872 2.607.33a18.095 18.095 0 005.223-5.223c.542-.827.369-1.908-.33-2.607L11.16 3.66A2.25 2.25 0 009.568 3z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 6h.008v.008H6V6z" />
                      </svg>
                      {category.name}
                    </Link>
                  )}
                  
                  {/* Subcategories - Only show when expanded */}
                  {category.children && category.children.length > 0 && expandedCategories.has(category.id) && (
                    <div className="ml-6 space-y-1 mt-1 animate-in slide-in-from-top-2 duration-200">
                      {category.children.map((subCategory) => (
                        <Link
                          key={subCategory.id}
                          to={`/product-signature?category=${subCategory.slug}`}
                          className="flex items-center px-4 py-2 text-sm text-gray-600 hover:text-gray-800 hover:bg-gray-50 rounded-lg transition-all duration-200"
                          onClick={() => handleCategoryClick(subCategory.slug)}
                        >
                          <span className="w-1.5 h-1.5 bg-gray-400 rounded-full mr-3"></span>
                          {subCategory.name}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className={`relative group ${className}`}>
      <button 
        className={`transition-colors text-sm font-medium flex items-center ${
          isActive 
            ? 'text-gray-900 border-b-2 border-gray-900 pb-1' 
            : 'text-gray-600 hover:text-gray-900'
        }`}
        onMouseEnter={() => setIsDropdownOpen(true)}
        onMouseLeave={() => setIsDropdownOpen(false)}
      >
        Products
        <ChevronDownIcon className={`ml-1 h-4 w-4 transition-transform duration-200 ${
          isDropdownOpen ? 'rotate-180' : 'rotate-0'
        }`} />
      </button>
      
      {/* Desktop Dropdown */}
      <div 
        className={`absolute top-full mt-2 bg-white shadow-lg border border-gray-200 rounded-lg z-50 transition-all duration-200 ${
          isDropdownOpen ? 'opacity-100 visible' : 'opacity-0 invisible'
        }`}
        style={{ 
          width: '1000px',
          left: '-200px'
        }}
        onMouseEnter={() => setIsDropdownOpen(true)}
        onMouseLeave={() => setIsDropdownOpen(false)}
      >
        {loading ? (
          <div className="p-8 text-center text-gray-500">
            Loading categories...
          </div>
        ) : (
          <div className="max-w-7xl mx-auto px-8 py-8 max-h-[70vh] overflow-y-auto pr-2">
            <div className="grid grid-cols-5 gap-12">
              {/* All Product Link as First Column */}
              <div className="space-y-4">
                <div className="block text-sm font-bold text-gray-900 pb-3 border-b border-gray-200 cursor-default">
                  ALL PRODUCTS
                </div>
                <div className="space-y-3">
                  <Link
                    to="/product-signature"
                    className="block text-xs text-gray-600 hover:text-gray-900 transition-colors leading-relaxed"
                    onClick={() => handleCategoryClick('all')}
                  >
                    View All Products
                  </Link>
                </div>
              </div>
              {categories.map((category) => (
                <div key={category.id} className="space-y-4">
                  <Link
                    to={`/product-signature?category=${category.slug}`}
                    className="block text-sm font-bold text-gray-900 pb-3 border-b border-gray-200 hover:text-gray-900"
                    onClick={() => handleCategoryClick(category.slug)}
                  >
                    {category.name.toUpperCase()}
                  </Link>
                  {category.children && category.children.length > 0 && (
                    <div className="space-y-3">
                      {category.children.map((subCategory) => (
                        <Link
                          key={subCategory.id}
                          to={`/product-signature?category=${subCategory.slug}`}
                          className="block text-xs text-gray-600 hover:text-gray-900 transition-colors leading-relaxed"
                          onClick={() => handleCategoryClick(subCategory.slug)}
                        >
                          {subCategory.name}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
            
            {categories.length === 0 && !loading && (
              <div className="p-8 text-center text-gray-500 text-sm">
                No categories available
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default CategoryNavigation;
